import React from 'react';
import CharacterAssets from '@/utils/CharacterAssets';
import type { CharacterInfo } from '@/utils/CharacterAssets';
import { PlayerAvatar } from './PlayerAvatar';
import { FaCheck } from 'react-icons/fa';

interface AvatarSelectorProps {
  selectedAvatar: string;
  onAvatarSelect: (avatarId: string) => void;
  disabled?: boolean;
}

export const AvatarSelector: React.FC<AvatarSelectorProps> = ({
  selectedAvatar,
  onAvatarSelect,
  disabled = false
}) => {
  const characterGrid = CharacterAssets.getCharacterGrid();

  const handleAvatarClick = (characterId: string) => {
    if (!disabled) {
      onAvatarSelect(characterId);
    }
  };

  const getCharacterInfo = (characterId: string): CharacterInfo | null => {
    return CharacterAssets.getCharacterInfo(characterId);
  };

  return (
    <div className="space-y-6">
      {/* Character Grid */}
      <div className="max-w-2xl mx-auto space-y-4">
        {characterGrid.map((row, rowIndex) => (
          <div key={rowIndex} className="flex gap-3 justify-center flex-wrap">
            {row.map((characterId, colIndex) => {
              const characterInfo = getCharacterInfo(characterId);
              if (!characterInfo) return null;

              const isSelected = selectedAvatar === characterId;

              return (
                <div
                  key={characterId}
                  className={`
                    relative flex flex-col items-center gap-3 p-4 rounded-lg cursor-pointer group min-w-[110px]
                    transition-all duration-200 ease-out
                    ${disabled 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-game-medium/40'
                    }
                    ${isSelected 
                      ? 'bg-game-golden/15 border-2 border-game-golden/50' 
                      : 'bg-game-medium/30 border-2 border-game-medium/50 hover:border-game-light/50'
                    }
                  `}
                  onClick={() => handleAvatarClick(characterId)}
                  title={`${characterInfo.displayName}: ${characterInfo.description}`}
                >
                  {/* Selection Indicator */}
                  {isSelected && (
                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-game-golden rounded-full flex items-center justify-center shadow-lg shadow-game-golden/30 border-2 border-white">
                      <FaCheck className="text-white text-sm font-bold" />
                    </div>
                  )}
                  
                  {/* Character Preview */}
                  <PlayerAvatar
                    username={characterInfo.displayName}
                    avatar={characterId}
                    size="lg"
                    className={`transition-all duration-200 ${
                      isSelected 
                        ? 'ring-2 ring-game-golden shadow-lg shadow-game-golden/40' 
                        : 'group-hover:shadow-md'
                    }`}
                  />
                  
                  {/* Character Name */}
                  <div className="text-center">
                    <div className={`text-xs font-semibold leading-tight ${
                      isSelected ? 'text-game-golden' : 'text-gray-200 group-hover:text-white'
                    }`}>
                      {characterInfo.displayName}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AvatarSelector;